import { AXON_EDC_URL, AXON_REGION, ENV } from '@env';

import { Injectable } from '@angular/core';

import { MxEventName, MxPropertyName, TrackProfile, mixpanelTrack } from '@axon-enterprise/track';
import { FsAxonDevice, SnCamera } from '@lib/api/entities';

const mixpanelConfig = {
  api_host: `https://telemetry${AXON_EDC_URL}/api/telemetry/mixpanel`,
};

@Injectable({
  providedIn: 'root',
})
export class MixpanelService {
  currentUrl: string | undefined = undefined;
  previousUrl: string | undefined = undefined;

  constructor() {
    mixpanelTrack.init('Axon Fusus', AXON_REGION, mixpanelConfig);
    mixpanelTrack.registerSafely({ [MxPropertyName.FususRegionName]: ENV });
    this.startSession();
  }

  setProfile(trackProfile: TrackProfile): void {
    mixpanelTrack.setProfile(trackProfile);
  }

  startSession(): void {
    mixpanelTrack.registerSafely({ [MxPropertyName.SessionID]: crypto.randomUUID() });
    mixpanelTrack.timeEventSafely(MxEventName.TotalTimeInAppAf);
  }

  endSession(): void {
    mixpanelTrack.trackSafely(MxEventName.TotalTimeInAppAf);
  }

  handlePageView(url: string): void {
    // We don't want to send URL hashes or parameters to Mixpanel
    this.currentUrl = url.split(new RegExp(/[#?]/))[0];
    if (this.currentUrl !== this.previousUrl) {
      mixpanelTrack.trackPageView({ url: this.currentUrl, pageName: this.currentUrl });
      this.previousUrl = this.currentUrl;
    }
  }

  public trackMetric(event: MxEventName, properties: Object): void {
    mixpanelTrack.trackSafely(event, properties);
  }

  public logSessionDuration(startTime: Date, pageName: String): void {
    const endTime = new Date();
    const totalTime = (endTime.getTime() - startTime.getTime()) / 1000;
    this.trackMetric(MxEventName.TimeOnPage, {
      [MxPropertyName.Duration]: totalTime,
      [MxPropertyName.PageName]: pageName,
    });
  }

  /**
   * Creates standardized tracking information for camera events
   * @param source The camera or Axon device being tracked
   * @param organizationId The ID of the current organization
   * @param organizationName The name of the current organization
   * @param endReason Optional reason for ending a stream
   * @param sourceUI Optional UI source that initiated the action
   * @returns Standardized tracking object for Mixpanel
   */
  public createCameraTrackingInfo(
    source: SnCamera | { axonDevice: FsAxonDevice },
    organizationId: string,
    organizationName: string,
    endReason?: string,
    sourceUI?: string,
  ): Record<string, any> {
    const isAxonCamera = 'isAxonCamera' in source ? source.isAxonCamera : true;
    const axonDevice =
      'axonDevice' in source
        ? source.axonDevice
        : (source as { axonDevice: FsAxonDevice }).axonDevice;

    const organization = 'organization' in source ? source.organization : undefined;
    const make = 'make' in source ? source.make : '';
    const model = 'model' in source ? source.model : '';
    const id = 'id' in source ? source.id : axonDevice?.axonDeviceId;
    const aiAppliance = 'aiAppliance' in source ? source.aiAppliance : undefined;

    return {
      [MxPropertyName.FususCarrier]: '',
      [MxPropertyName.FususViewerOrganizationID]: organizationId,
      [MxPropertyName.FususCamOrgViewer]: organizationName,
      [MxPropertyName.FususBrowserDownlinkSpeed]:
        (navigator as any)['connection']?.downlink || 'Unsupported Browser',
      [MxPropertyName.FususRTT]: '',
      [MxPropertyName.FususStreamID]: '',
      [MxPropertyName.FususStreamStartUI]: sourceUI || '',
      [MxPropertyName.FususStreamEndReason]: endReason || '',
      [MxPropertyName.FususStreamDuration]: '',

      [MxPropertyName.FususConnectionType]: isAxonCamera
        ? axonDevice?.attributes?.signalStrengths?.[0]?.networkType ?? ''
        : '',
      [MxPropertyName.FususConnectionStrength]: isAxonCamera
        ? axonDevice?.attributes?.signalStrengths?.[0]?.signalStrength ?? ''
        : '',
      [MxPropertyName.FususCamOwnerID]: isAxonCamera
        ? axonDevice?.attributes?.assignees?.[0]?.userId ?? ''
        : organization?.id,
      [MxPropertyName.FususCamOrgOwner]: isAxonCamera
        ? axonDevice?.partnerName ?? ''
        : organization?.name,
      [MxPropertyName.FususDeviceCategory]: '',
      [MxPropertyName.FususDeviceBrand]: isAxonCamera ? 'Axon' : make,
      [MxPropertyName.DeviceModel]: isAxonCamera
        ? axonDevice?.attributes?.deviceModel ?? ''
        : model,
      [MxPropertyName.FususIntegration]: isAxonCamera ? 'axonconnect' : '',
      [MxPropertyName.FususLivestreamID]: isAxonCamera
        ? axonDevice?.attributes?.correlation?.id ?? ''
        : '',
      [MxPropertyName.DeviceID]: isAxonCamera ? axonDevice?.axonDeviceId ?? '' : id,
      [MxPropertyName.FususCoreID]: isAxonCamera ? '' : aiAppliance ?? '',
    };
  }

  /**
   * Sets metadata that will be included with every Mixpanel event.
   * Unlike setProfile which is used for user-specific properties, this method
   * can be used to set any Mixpanel property that should be included in all events.
   *
   * @param metadata - An object containing Mixpanel properties to be included with all events
   */
  public setMetadata(metadata: Parameters<typeof mixpanelTrack.registerSafely>[0]): void {
    mixpanelTrack.registerSafely(metadata);
  }
}
